<template>
  <div class="min-h-screen bg-cover bg-center bg-no-repeat relative" 
       :style="{ backgroundImage: 'url(/images/login-bg.jpg)' }">
    <!-- Dark overlay for better readability -->
    <div class="absolute inset-0 bg-black bg-opacity-40"></div>
    
    <!-- Header with branding and language switcher -->
    <div class="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-6">
      <div class="flex justify-between items-center">
        <!-- SONNENGLAS branding -->
        <div class="text-white">
          <h1 class="text-lg font-semibold">SONNENGLAS® Partner Portal</h1>
        </div>
        
        <!-- Language switcher -->
        <div class="flex items-center space-x-2">
          <a href="#" 
             @click.prevent="switchLanguage('de')"
             class="text-white hover:text-gray-200 text-sm font-medium px-2 py-1 rounded"
             :class="{ 'bg-white bg-opacity-20': currentLocale === 'de' }">
            🇩🇪 DE
          </a>
          <a href="#" 
             @click.prevent="switchLanguage('en')"
             class="text-white hover:text-gray-200 text-sm font-medium px-2 py-1 rounded"
             :class="{ 'bg-white bg-opacity-20': currentLocale === 'en' }">
            🇺🇸 EN
          </a>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div class="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div class="mb-6">
          <h2 class="text-center text-2xl font-bold text-gray-900 mb-2">
            Reset Password
          </h2>
          <p class="text-center text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>
        
        <!-- Success message -->
        <div v-if="status" class="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div class="text-green-800 text-sm">
            {{ status }}
          </div>
        </div>
        
        <form class="space-y-6" @submit.prevent="submit">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="email"
              v-model="form.email"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter your email address"
            />
            <div v-if="form.errors.email" class="text-red-600 text-sm mt-1">
              {{ form.errors.email }}
            </div>
          </div>

          <div>
            <button
              type="submit"
              :disabled="form.processing"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="form.processing" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
              <span v-else>Send Password Reset Email</span>
            </button>
          </div>
          
          <!-- Back to login link -->
          <div class="text-center">
            <div class="text-sm text-gray-600">
              Remember your password? 
              <a href="#" 
                 @click.prevent="goToLogin"
                 class="font-medium text-blue-600 hover:text-blue-500 ml-1">
                Sign in
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Footer branding -->
    <div class="absolute bottom-6 left-6 z-10">
      <div class="text-white text-sm opacity-80">
        <span class="font-semibold">Sonnenglas</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useForm, router } from '@inertiajs/vue3'
import { ref, onMounted, inject } from 'vue'

// Inject Ziggy route helper for script setup
const route = inject('route') as any

interface Props {
  status?: string
}

const { status } = defineProps<Props>()

const currentLocale = ref('en')

const form = useForm({
  email: '',
})

onMounted(() => {
  // Get current locale from browser or localStorage
  currentLocale.value = localStorage.getItem('locale') || 'en'
})

const submit = () => {
  form.post(route('password.email'), {
    onFinish: () => {
      // Don't reset the form on success so user can see their email
    },
  })
}

const switchLanguage = (locale: string) => {
  currentLocale.value = locale
  localStorage.setItem('locale', locale)

  // Redirect to current page with new locale
  const currentUrl = new URL(window.location.href)
  currentUrl.searchParams.set('lang', locale)
  window.location.href = currentUrl.toString()
}

const goToLogin = () => {
  router.get(route('login'))
}
</script>